/**
 * The three ways into Pollinator, as Outlast's copy names them.
 *
 * Each card offers two buttons: the quieter one gives somebody who is not ready
 * to start a place to go, and the louder one starts the form. Only the forms
 * carry a slug, which is what lets the funnel report attribute a click to an
 * audience rather than counting every call to action on the site together.
 */
export type AudienceKey = "participants" | "organizations" | "funders";

export type AudienceCta = {
  label: string;
  href: string;
  /** The configured form this leads to, where it leads to one */
  formSlug?: string;
  variant?: "primary" | "secondary";
};

export type Audience = {
  key: AudienceKey;
  /** The audience, as the eyebrow names them */
  title: string;
  /** The claim made to this audience on the homepage card */
  heading?: string;
  /** The line of argument under it, which only the card shows */
  description?: string;
  /**
   * What the foot of a dark panel says in place of `heading`, where the two
   * surfaces argue differently. A panel shows this on its own, with no line
   * beneath it; a card keeps `heading` and `description`.
   */
  panelHeading?: string;
  /**
   * What the card beside the audience's own section says in place of
   * `description`. That card is the width of the page where the hero's is one
   * of three in a row, so it can carry a longer line of argument.
   */
  sectionDescription?: string;
  ctas: AudienceCta[];
};

export const marketingAudienceCtasData: Audience[] = [
  {
    key: "participants",
    title: "Artists + Changemakers",
    heading: "Connect as a peer participant.",
    description:
      "The Pollinator Program builds trust and connection between participants through peer coaching.",
    sectionDescription:
      "The Pollinator Program builds trust and connection between participants through peer coaching, because when we show up for each other, we can do anything.",
    ctas: [
      // Learn More reaches the participants' own section, which is what the
      // nav offers under Network as Participants.
      { label: "Learn More", href: "/#participants", variant: "secondary" },
      { label: "Join as a Participant", href: "/join", formSlug: "join" },
    ],
  },
  {
    key: "organizations",
    title: "Culture + Social Impact Organizations",
    heading: "Add your organization to our network.",
    description:
      "Support your alumni, grantees, or members with access to Pollinator — at no cost to you, or to your people.",
    panelHeading:
      "Culture and social impact organizations support their people by inviting them to Pollinator.",
    ctas: [
      { label: "Learn More", href: "/#organizations", variant: "secondary" },
      {
        label: "Add Your People",
        href: "/add-your-people",
        formSlug: "add_your_people",
      },
    ],
  },
  {
    key: "funders",
    title: "Foundations + Grantmakers",
    heading: "Match people to your mission.",
    description:
      "We source qualified grantees and offer strategic field insights to funders who center trust.",
    ctas: [
      { label: "Learn More", href: "/#fund", variant: "secondary" },
      {
        label: "Request a Meeting",
        href: "/partner-with-us",
        formSlug: "partner_with_us",
      },
    ],
  },
];
